import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DemandeType } from '@models/enums';
import { HttpParamsUtils } from 'src/app/shared/utils/http-params-utils';
import { FilterParams } from '@models/api/requests';
import { DemandeValidation } from '@models/api/responses';
import { PagedResultDTO } from '@models/shared';
import { 
  DemandeAvisValidation, 
  DemandeToValidateCountResult,
  DemandeAvisCountResult 
} from '@models/api/responses';
import {
  CreateDemandeAvisRequest,
  AnswerDemandeAvisRequest,
  DemandeAvisReponse,
  DemandeAvisItem
} from '@models/api/requests';

interface DeleteDemandesRequest {
  Recrutement?: string[];
  Renouvellement?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class DemandesService {
  module = environment.webService.server + environment.webService.url + environment.webService.api;
  constructor(private readonly http: HttpClient) {}

  // Add service methods here as needed

  deleteDemandes(requestBody: DeleteDemandesRequest): Observable<any> {
    return this.http.delete<any>(`${this.module}/demandes`, { body: requestBody });
  }

  createAdvice(requestBody: CreateDemandeAvisRequest): Observable<any> {
    return this.http.post<CreateDemandeAvisRequest>(`${this.module}/demandes/demande-avis`, requestBody);
  }
  answerDemandeAvis(idDemande: string, demandeAvisItem: AnswerDemandeAvisRequest): Observable<any> {
    const formdata = new FormData();
    formdata.append('demandeAvisReponse', JSON.stringify(demandeAvisItem.demandeAvisReponse));
    demandeAvisItem.piecesJointes?.forEach((file, index) => {
      formdata.append(`piecesJointes`, file);
    });
    return this.http.post<any>(`${this.module}/demandes/demande-avis/${idDemande}`, formdata);
  }

  getDemandesAValider(page: number = 0, size: number = 10, filters?: FilterParams): Observable<PagedResultDTO<DemandeValidation>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    params = HttpParamsUtils.addTypedFilterParams(params, filters);
    return this.http.get<PagedResultDTO<DemandeValidation>>(`${this.module}/demandes/a-valider`, { params });
  }

  getDemandeToValidateCount(): Observable<DemandeToValidateCountResult> {
    // Use a large size to get all records in a single request
    const params = new HttpParams().set('page', '0').set('size', '1000'); // Increased size to ensure all are fetched

    return this.http.get<PagedResultDTO<DemandeValidation>>(`${this.module}/demandes/a-valider`, { params }).pipe(
      map((response) => {
        const numberRecrutementToValidate = response.results.filter((demande) => demande.typeDemande === DemandeType.Recrutement).length;

        const numberRenouvellementToValidate = response.results.filter(
          (demande) => demande.typeDemande === DemandeType.Renouvellement
        ).length;

        return { numberRecrutementToValidate, numberRenouvellementToValidate }; // Removed total calculation
      })
    );
  }

  getDemandeAvisToValidateCount(): Observable<DemandeAvisCountResult> {
    const params = new HttpParams().set('page', '0').set('size', '1000'); // Increased size
    return this.http.get<PagedResultDTO<DemandeAvisValidation>>(`${this.module}/demandes/demande-avis`, { params }).pipe(
      map((response) => {
        const demandeAvis = response.results;
        const totalDemandeAvis = demandeAvis.length;
        const numberRecrutementAvis = demandeAvis.filter((avis) => avis.typeDemande === DemandeType.Recrutement).length;
        const numberRenouvellementAvis = demandeAvis.filter((avis) => avis.typeDemande === DemandeType.Renouvellement).length;

        return { demandeAvis, totalDemandeAvis, numberRecrutementAvis, numberRenouvellementAvis };
      })
    );
  }

  // New method for paginated advice requests
  getDemandesAvis(page: number = 0, size: number = 10): Observable<PagedResultDTO<DemandeValidation>> {
    const params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    // Expecting response structure similar to DemandeValidation + advice fields
    return this.http.get<PagedResultDTO<DemandeValidation>>(`${this.module}/demandes/demande-avis`, { params });
  }

  cancelDemandeAvis(idDemande: string, typeDemande: DemandeType): Observable<any> {
    return this.http.delete<any>(`${this.module}/demandes/demande-avis/${idDemande}/${typeDemande}`);
  }
}
